/**
 * Rich Matching Schema
 * A content type that combines lesson content (Page) with matching questions
 */

import type { ContentConfig, LayoutConfig, ScoringConfig } from '@ai-content-hub/content-core';
import type { PageElementData, MatchElementData } from '@ai-content-hub/content-elements';

/**
 * Player view layout: how lesson and questions are displayed
 * - tabs: Two tabs - switch between lesson and questions
 * - stacked: Lesson on top, questions below (default)
 * - split: Side-by-side, lesson left and questions right
 */
export type PlayerViewLayout = 'tabs' | 'stacked' | 'split';

/**
 * Layout configuration for Rich Matching
 */
export interface RichMatchingLayoutConfig extends LayoutConfig {
    /** Player view layout */
    playerView?: PlayerViewLayout;
    /** Whether to stack panels vertically on mobile */
    responsive?: boolean;
}

/**
 * Scoring configuration for Rich Matching
 */
export interface RichMatchingScoringConfig extends ScoringConfig {
    /** Points per correct match (default: 1) */
    pointsPerMatch?: number;
    /** Passing threshold (0-1) */
    passingThreshold?: number;
}

/**
 * Rich Matching Config
 */
export interface RichMatchingConfig extends Omit<ContentConfig, 'layout' | 'scoring'> {
    layout?: RichMatchingLayoutConfig;
    scoring?: RichMatchingScoringConfig;
}

/**
 * Rich Matching Data
 */
export interface RichMatchingData {
    /** Optional title */
    title?: string;
    /** Optional description / instructions */
    description?: string;
    /** Lesson content using Page element */
    lessonContent: PageElementData;
    /** Matching questions */
    matchData: MatchElementData;
}

/**
 * Rich Matching Content - complete content item
 */
export interface RichMatchingContent {
    id: string;
    type: 'rich-matching';
    version: string;
    data: RichMatchingData;
    config?: RichMatchingConfig;
}

/**
 * Default i18n translations
 */
export const defaultRichMatchingI18n = {
    en: {
        lessonTab: 'Lesson',
        questionsTab: 'Questions',
        submit: 'Submit Answers',
        retry: 'Try Again',
        score: 'Score',
        passed: 'Passed!',
        failed: 'Keep practicing',
        configuration: 'Configuration',
        title: 'Title',
        enterTitlePlaceholder: 'Enter title',
        descriptionOptional: 'Description (Optional)',
        enterDescriptionPlaceholder: 'Enter description or instructions for learners',
    },
    vi: {
        lessonTab: 'Nội dung',
        questionsTab: 'Câu hỏi',
        submit: 'Gửi câu trả lời',
        retry: 'Thử lại',
        score: 'Điểm',
        passed: 'Đã vượt qua!',
        failed: 'Tiếp tục luyện tập',
        configuration: 'Cấu hình',
        title: 'Tiêu đề',
        enterTitlePlaceholder: 'Nhập tiêu đề câu hỏi',
        descriptionOptional: 'Hướng dẫn bài tập (Không bắt buộc)',
        enterDescriptionPlaceholder: 'Nhập hướng dẫn bài tập...',
    },
};

/**
 * Default config
 */
export const defaultRichMatchingConfig: RichMatchingConfig = {
    layout: {
        type: 'horizontal',
        playerView: 'stacked',
        responsive: true,
    },
    behavior: {
        allowRetry: true,
        showCorrectAnswer: true,
        showExplanation: true,
    },
    scoring: {
        pointsPerMatch: 1,
        partialCreditEnabled: true,
        passingThreshold: 0.7,
    },
    i18n: {
        defaultLanguage: 'en',
        languages: defaultRichMatchingI18n,
    },
};

/**
 * Default data
 */
export const defaultRichMatchingData: RichMatchingData = {
    title: '',
    description: '',
    lessonContent: {
        title: '',
        description: '',
        sections: [],
    },
    matchData: {
        items: [],
        options: [],
        shuffleOptions: true,
        oneToOne: true,
    },
};

/**
 * Schema definition
 */
export const richMatchingSchema = {
    type: 'rich-matching',
    title: 'Matching',
    description: 'A content type with lesson content and matching questions',
    version: '1.0.0',
    category: 'assessment',
    icon: 'BookOpen',
    tags: ['lesson', 'matching', 'assessment'],
    defaultData: defaultRichMatchingData,
    defaultConfig: defaultRichMatchingConfig,
};
